import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { X, Gift, Copy, Check, MessageCircle, Mail, User } from "lucide-react";
import pixelService from "../services/pixelService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DONE_KEY = "ms_lead_done";
const DISMISS_KEY = "ms_lead_dismissed";
const HIDDEN_PREFIXES = ["/painel", "/checkout", "/payment"];

const formatPhone = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

// Storefront lead capture: e-mail + WhatsApp in exchange for a coupon. Every later pixel/CAPI
// event from this browser is enriched with the captured identifiers (Advanced Matching).
export const LeadCapturePopup = () => {
  const location = useLocation();
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [copied, setCopied] = useState(false);

  const hidden = HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (localStorage.getItem(DONE_KEY) || sessionStorage.getItem(DISMISS_KEY)) return;
    fetch(`${API}/lead-popup`).then((r) => r.json()).then((d) => d?.enabled && setCfg(d)).catch(() => {});
  }, []);

  const show = useCallback(() => {
    if (localStorage.getItem(DONE_KEY) || sessionStorage.getItem(DISMISS_KEY)) return;
    setOpen(true);
  }, []);

  useEffect(() => {
    if (!cfg || hidden || open) return;
    const t = setTimeout(show, Math.max(1, Number(cfg.delay_seconds) || 8) * 1000);
    const onLeave = (e) => { if (e.clientY <= 0) show(); };
    document.addEventListener("mouseout", onLeave);
    return () => { clearTimeout(t); document.removeEventListener("mouseout", onLeave); };
  }, [cfg, hidden, open, show]);

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    const email = form.email.trim().toLowerCase();
    const phoneDigits = form.phone.replace(/\D/g, "");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return setError("Digite um e-mail válido");
    if (phoneDigits.length < 10) return setError("Digite um WhatsApp válido com DDD");
    setError("");
    setSending(true);
    try {
      const attr = pixelService.getAttribution();
      pixelService.setUserData({ name: form.name, email, phone: phoneDigits });
      const params = new URLSearchParams(window.location.search);
      const utm = {};
      ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "src", "sck"].forEach((k) => { if (params.get(k)) utm[k] = params.get(k); });
      const resp = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: form.name.trim(), email, phone: phoneDigits, fbp: attr.fbp, fbc: attr.fbc, client_ip: attr.client_ip, source_url: attr.event_source_url, utm_data: utm }),
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error(d.detail || "Não foi possível salvar. Tente novamente.");
      }
      pixelService.trackLead({ content_name: "lead_popup" });
      localStorage.setItem(DONE_KEY, "1");
      if (cfg.coupon_code) localStorage.setItem("ms_lead_coupon", cfg.coupon_code);
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(cfg.coupon_code); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (_) {}
  };

  if (!cfg || hidden || !open) return null;

  return (
    <div className="fixed inset-0 z-[9998] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4" data-testid="lead-popup-overlay" onClick={dismiss}>
      <div
        className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-8 sm:zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
        data-testid="lead-popup"
      >
        <button type="button" aria-label="Fechar" onClick={dismiss} className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/80 hover:bg-gray-100 flex items-center justify-center text-gray-500" data-testid="lead-popup-close">
          <X size={18} />
        </button>

        <div className="px-6 pt-8 pb-5 text-center" style={{ background: "var(--color-primary-10, #EEF2FF)" }}>
          <div className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-lg" style={{ background: "var(--color-primary, #0047FF)" }}>
            <Gift size={26} />
          </div>
          <h3 className="mt-4 text-2xl font-black text-gray-900 leading-tight" data-testid="lead-popup-title">{cfg.title}</h3>
          <p className="mt-1 text-sm text-gray-600" data-testid="lead-popup-subtitle">{cfg.subtitle}</p>
        </div>

        {done ? (
          <div className="px-6 py-6 text-center" data-testid="lead-popup-success">
            <p className="text-sm text-gray-600 mb-3">Seu cupom está liberado. Use no checkout:</p>
            <button type="button" onClick={copy} className="w-full border-2 border-dashed rounded-2xl py-4 flex items-center justify-center gap-3 hover:bg-gray-50" style={{ borderColor: "var(--color-primary, #0047FF)" }} data-testid="lead-popup-coupon">
              <span className="text-2xl font-black tracking-widest" style={{ color: "var(--color-primary, #0047FF)" }}>{cfg.coupon_code}</span>
              {copied ? <Check size={18} className="text-emerald-600" /> : <Copy size={18} className="text-gray-400" />}
            </button>
            {cfg.discount_text && <p className="mt-2 text-sm font-bold text-orange-600">{cfg.discount_text}</p>}
            <button type="button" onClick={() => setOpen(false)} className="mt-5 w-full py-3 rounded-xl text-white font-bold" style={{ background: "var(--color-primary, #0047FF)" }} data-testid="lead-popup-continue">
              Continuar comprando
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-6 space-y-3" data-testid="lead-popup-form">
            {cfg.ask_name && (
              <label className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2.5 focus-within:border-gray-400">
                <User size={16} className="text-gray-400" />
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Seu nome" autoComplete="name" className="flex-1 text-sm outline-none bg-transparent" data-testid="lead-popup-name" />
              </label>
            )}
            <label className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2.5 focus-within:border-gray-400">
              <MessageCircle size={16} className="text-gray-400" />
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })} placeholder="WhatsApp com DDD" inputMode="tel" autoComplete="tel" className="flex-1 text-sm outline-none bg-transparent" data-testid="lead-popup-phone" />
            </label>
            <label className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2.5 focus-within:border-gray-400">
              <Mail size={16} className="text-gray-400" />
              <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Seu melhor e-mail" inputMode="email" autoComplete="email" className="flex-1 text-sm outline-none bg-transparent" data-testid="lead-popup-email" />
            </label>
            {error && <p className="text-xs text-red-600" data-testid="lead-popup-error">{error}</p>}
            <button type="submit" disabled={sending} className="w-full py-3.5 rounded-xl text-white font-bold disabled:opacity-60 transition-transform active:scale-[0.98]" style={{ background: "var(--color-primary, #0047FF)" }} data-testid="lead-popup-submit">
              {sending ? "Enviando..." : `Quero meu cupom${cfg.discount_text ? ` de ${cfg.discount_text}` : ""}`}
            </button>
            <p className="text-[11px] text-gray-400 text-center">Sem spam. Você pode sair quando quiser.</p>
          </form>
        )}
      </div>
    </div>
  );
};

export default LeadCapturePopup;
